// src/components/queries/QueryAnswersOverlay.js
// The answers to one query: for one resident (patientId) or, for a query
// created for several residents, everyone in its series side by side.
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

const API = process.env.REACT_APP_API_URL;
const RANGES = [
  { days: 7, label: "7 Tage" },
  { days: 30, label: "30 Tage" },
  { days: 90, label: "90 Tage" },
];
const VIA_LABELS = { voice: "Sprache", touch: "Touch", button: "Taste" };

function cls(...a) {
  return a.filter(Boolean).join(" ");
}

/** What a resident's ask came to, in one short label. */
export function answerLabel(row) {
  switch (row.status) {
    case "answered":
      return row.asked?.answer_type === "scale" ? `${row.answer} von ${row.asked?.scale?.max}` : String(row.answer);
    case "declined":
      return "Abgelehnt";
    case "open":
      return "Wird gefragt";
    case "no_answer":
      // Never asked at all: the device was offline or busy the whole time.
      return row.attempts ? "Keine Antwort" : "Nicht gefragt";
    default:
      return "—";
  }
}

function statusTone(row) {
  if (row.status === "answered") return "bg-green-50 text-green-800 border-green-200";
  if (row.status === "declined") return "bg-amber-50 text-amber-800 border-amber-200";
  if (row.status === "open") return "bg-blue-50 text-blue-800 border-blue-200";
  return "bg-gray-100 text-gray-700 border-gray-200";
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("de-DE", {
    weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
  });
}

function formatTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
}

function Summary({ rows, config }) {
  const counts = {
    answered: rows.filter((r) => r.status === "answered").length,
    declined: rows.filter((r) => r.status === "declined").length,
    noAnswer: rows.filter((r) => r.status === "no_answer" && r.attempts > 0).length,
    notAsked: rows.filter((r) => r.status === "no_answer" && !r.attempts).length,
  };
  const answered = rows.filter((r) => r.status === "answered");
  const isScale = config?.answer_type === "scale";

  // How often each answer was given; the configured answers keep their order,
  // answers to an earlier version of the question follow.
  const tally = new Map((isScale ? [] : config?.options || []).map((option) => [String(option), 0]));
  for (const row of answered) tally.set(String(row.answer), (tally.get(String(row.answer)) || 0) + 1);
  const bars = isScale
    ? [...tally.entries()].sort((a, b) => Number(a[0]) - Number(b[0]))
    : [...tally.entries()];
  const most = Math.max(1, ...bars.map(([, n]) => n));
  const average = isScale && answered.length
    ? (answered.reduce((sum, r) => sum + Number(r.answer), 0) / answered.length).toFixed(1).replace(".", ",")
    : null;

  return (
    <div className="grid gap-3 md:grid-cols-[auto_1fr]">
      <div className="grid grid-cols-2 gap-2 text-sm md:w-72">
        {[
          ["Beantwortet", counts.answered],
          ["Abgelehnt", counts.declined],
          ["Keine Antwort", counts.noAnswer],
          ["Nicht gefragt", counts.notAsked],
        ].map(([label, value]) => (
          <div key={label} className="rounded-lg bg-white px-3 py-2">
            <div className="text-xs text-gray-600">{label}</div>
            <div className="text-xl font-semibold text-gray-900">{value}</div>
          </div>
        ))}
      </div>
      <div className="rounded-lg bg-white px-3 py-2 text-sm">
        <div className="mb-2 flex items-baseline justify-between text-xs text-gray-600">
          <span>Wie oft welche Antwort</span>
          {average && <span>Durchschnitt {average}</span>}
        </div>
        {bars.length === 0 ? (
          <div className="text-gray-600">Noch keine Antworten in diesem Zeitraum.</div>
        ) : (
          <div className="grid gap-1.5">
            {bars.map(([answer, n]) => (
              <div key={answer} className="grid grid-cols-[7rem_1fr_2rem] items-center gap-2">
                <span className="truncate text-gray-800" title={answer}>{answer}</span>
                <span className="h-2.5 rounded-full bg-blue-100">
                  <span className="block h-2.5 rounded-full bg-[#76B3FF]" style={{ width: `${(n / most) * 100}%` }} />
                </span>
                <span className="text-right font-semibold text-gray-900">{n}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function QueryAnswersOverlay({ reminder, patientId = null, onClose }) {
  const [days, setDays] = useState(30);
  const [rows, setRows] = useState([]);
  const [names, setNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const group = !patientId;

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let alive = true;
    const from = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
    const url = group
      ? `${API}/query-answers?series_id=${encodeURIComponent(reminder.series_id)}&from=${encodeURIComponent(from)}`
      : `${API}/patients/${patientId}/query-answers?reminder_id=${reminder.id}&from=${encodeURIComponent(from)}`;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(url, { credentials: "include" });
        if (!res.ok) throw new Error(`Antworten konnten nicht geladen werden (${res.status}).`);
        const data = await res.json();
        if (alive) setRows(Array.isArray(data?.items) ? data.items : []);
      } catch (e) {
        if (alive) setError(e?.message || "Antworten konnten nicht geladen werden.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [days, group, patientId, reminder.id, reminder.series_id]);

  useEffect(() => {
    if (!group || !reminder.series_id) return undefined;
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}/reminder-series/${encodeURIComponent(reminder.series_id)}/patients`, {
          credentials: "include",
        });
        if (!res.ok) return;
        const people = await res.json();
        if (alive) {
          setNames(Object.fromEntries(people.map((p) => [p.id, `${p.forename || ""} ${p.surname || ""}`.trim()])));
        }
      } catch {
        // Names are a nicety; the table still shows the answers.
      }
    })();
    return () => { alive = false; };
  }, [group, reminder.series_id]);

  const config = reminder.query_config;
  const sorted = useMemo(
    () => [...rows].sort((a, b) => (a.scheduled_at < b.scheduled_at ? 1 : -1)),
    [rows]
  );

  // Rendered into <body>: the page card's backdrop blur would otherwise pin
  // this "fixed" overlay to the card instead of the window, often off-screen.
  return createPortal(
    <div className="fixed inset-0 z-40" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div
        className="absolute left-1/2 top-6 z-50 max-h-[92vh] w-[min(1000px,92vw)] -translate-x-1/2 overflow-y-auto rounded-xl border border-black/10 bg-[#e9f4ff] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-black/10 px-4 py-3">
          <div>
            <div className="text-lg font-semibold text-[#0b1b2d]">
              Antworten: {reminder.title}{group ? " (alle Bewohner)" : ""}
            </div>
            {config?.question && <div className="text-sm text-gray-700">„{config.question}“</div>}
          </div>
          <div className="flex items-center gap-2">
            <select
              aria-label="Zeitraum"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="rounded-md border border-blue-200 bg-white px-3 py-1.5 text-sm text-gray-700"
            >
              {RANGES.map((range) => (
                <option key={range.days} value={range.days}>{range.label}</option>
              ))}
            </select>
            <button
              onClick={onClose}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-white/70"
            >
              Schließen
            </button>
          </div>
        </div>

        <div className="grid gap-4 px-4 py-4">
          {loading && <div className="text-sm text-gray-700">Lade Antworten…</div>}
          {error && !loading && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>
          )}

          {!loading && !error && (
            <>
              <Summary rows={sorted} config={config} />
              {sorted.length === 0 ? (
                <div className="rounded-lg bg-white/70 px-3 py-4 text-sm text-gray-700">
                  In diesem Zeitraum wurde noch nicht gefragt.
                </div>
              ) : (
                <div className="overflow-x-auto rounded-lg bg-white">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-[#d8edff] text-gray-700">
                      <tr>
                        <th className="px-3 py-2 font-medium">Geplant</th>
                        {group && <th className="px-3 py-2 font-medium">Bewohner</th>}
                        <th className="px-3 py-2 font-medium">Antwort</th>
                        <th className="px-3 py-2 font-medium">Versuche</th>
                        <th className="px-3 py-2 font-medium">Beantwortet</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map((row) => (
                        <tr key={row.id} className="border-t border-black/5">
                          <td className="px-3 py-2 text-gray-900">{formatDate(row.scheduled_at)}</td>
                          {group && <td className="px-3 py-2 text-gray-900">{names[row.patient_id] || `#${row.patient_id}`}</td>}
                          <td className="px-3 py-2">
                            <span className={cls("inline-block rounded-full border px-2.5 py-0.5 font-medium", statusTone(row))}>
                              {answerLabel(row)}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-gray-700">{row.attempts}</td>
                          <td className="px-3 py-2 text-gray-700">
                            {row.answered_at
                              ? `${formatTime(row.answered_at)} · ${VIA_LABELS[row.answered_via] || row.answered_via}`
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
